import { useEffect, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { mergeSx } from 'ui/merge-sx';

import ErrorWidget from 'containers/user/home/components/widgets/error-widget';
import NavButton from 'containers/user/home/components/shared/navigation-button';

import { getUserWidgetContent } from 'api/generated/user-widgets/user-widgets';

import { useReports } from 'hooks/api/use-reports';

import powerbi, { pbiModels } from 'utils/powerbi';

export const powerBiWidgetConfig = {
  colSpan: 2,
  rowSpan: {
    default: 9,
  },
};

const OVERLAY_SX: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'common.white',
  zIndex: 10,
  borderRadius: '0.5rem',
  transitionProperty: 'opacity',
  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  transitionDuration: '300ms',
};

const VISUAL_SX: SxProps<Theme> = {
  height: '100%',
  transitionProperty: 'opacity',
  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  transitionDuration: '300ms',
};

const HIDDEN_SX: SxProps<Theme> = { opacity: 0, pointerEvents: 'none' };

const Skeleton = (): React.JSX.Element => (
  <div className="animate-pulse w-full h-full flex flex-col space-y-4 p-4 bg-gray-50 rounded-lg">
    <Box
      sx={{
        height: '1.5rem',
        bgcolor: 'tokens.gray.300',
        borderRadius: '0.25rem',
        width: '33.333333%',
      }}
    />
    <Box
      sx={{
        height: '1rem',
        bgcolor: 'tokens.gray.300',
        borderRadius: '0.25rem',
        width: '66.666667%',
      }}
    />
    <Box
      sx={{
        height: '12rem',
        bgcolor: 'tokens.gray.200',
        borderRadius: '0.25rem',
      }}
    />
    <Box
      sx={{
        height: '1rem',
        bgcolor: 'tokens.gray.300',
        borderRadius: '0.25rem',
        width: '25%',
      }}
    />
  </div>
);

interface PowerBiTokenInfo {
  token: string;
}

interface PowerBiWidgetData {
  powerbiToken: PowerBiTokenInfo;
  powerbiReportId: string;
  powerbiPageId: string;
  powerbiVisualId: string;
  powerbiRelatedReportId?: string;
  powerbiRelatedReportPageId?: string;
}

interface RelatedReport {
  id: string | number;
  powerBiId: string;
  [key: string]: unknown;
}

interface PowerBiWidgetProps {
  userId: string;
  widgetId: string | number;
  sx?: SxProps<Theme>;
}

const PowerBiWidget = ({
  userId,
  widgetId,
  sx,
}: PowerBiWidgetProps): React.JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [widgetData, setWidgetData] = useState<PowerBiWidgetData | null>(null);
  const [relatedReport, setRelatedReport] = useState<
    RelatedReport | undefined
  >();

  const reports = useReports();

  const visualRef = useRef<HTMLDivElement>(null);

  const loadWidgetData = async (): Promise<void> => {
    setLoading(true);
    try {
      const body = (await getUserWidgetContent(userId, Number(widgetId))) as {
        widget?: { content?: PowerBiWidgetData };
      };
      const data = body.widget?.content;

      const reportsList = reports.data as RelatedReport[] | null | undefined;

      if (data && reportsList?.length && data.powerbiRelatedReportId) {
        setRelatedReport(
          reportsList.find(
            (rpt) => rpt.powerBiId === data.powerbiRelatedReportId
          )
        );
      }

      if (data) setWidgetData(data);
    } catch (err) {
      console.error('Error fetching widget data:', err);
      setError(err);
      setLoading(false);
    }
  };

  const loadVisual = (): void => {
    if (!widgetData || !visualRef.current) return;

    const { powerbiToken, powerbiReportId, powerbiPageId, powerbiVisualId } =
      widgetData;

    const embedConfiguration = {
      type: 'visual',
      id: powerbiReportId,
      embedUrl: 'https://app.powerbi.com/visualEmbed',
      tokenType: pbiModels.TokenType.Embed,
      accessToken: powerbiToken.token,
      pageName: powerbiPageId,
      visualName: powerbiVisualId,
      settings: {
        background: pbiModels.BackgroundType.Transparent,
        navContentPaneEnabled: false,
        filterPaneEnabled: false,
      },
    };

    try {
      const visual = powerbi.embed(visualRef.current, embedConfiguration);

      visual.on('rendered', () => setLoading(false));
      visual.on('error', (errorObject: { detail: unknown }) =>
        setError(errorObject.detail)
      );
    } catch (e) {
      console.error('Error embedding Power BI visual:', e);
      setError(e);
    }
  };

  useEffect(() => {
    if (error) return;

    if (widgetData) {
      loadVisual();
    } else {
      loadWidgetData();
    }
  }, [widgetData, error, reports.data]);

  useEffect(() => {
    if (visualRef.current && !error) {
      powerbi.bootstrap(visualRef.current, { type: 'visual' });
    }
  }, [error]);

  if (error) {
    return (
      <Box
        sx={{
          p: 2.5,
          height: '100%',
          borderWidth: '1px',
          borderColor: 'tokens.navy.200',
          borderRadius: '0.5rem',
        }}
      >
        <ErrorWidget />
      </Box>
    );
  }

  return (
    <Stack
      sx={{
        position: 'relative',
        height: '100%',
        p: 2.5,
        borderWidth: '1px',
        borderColor: 'tokens.navy.200',
        borderRadius: '0.5rem',
        gap: 1.25,
      }}
    >
      {loading && (
        <Box sx={OVERLAY_SX}>
          <Skeleton />
        </Box>
      )}
      <Box
        ref={visualRef}
        sx={mergeSx(VISUAL_SX, loading ? HIDDEN_SX : undefined, sx)}
      />
      {relatedReport && (
        <NavButton
          link={`/reports/${relatedReport?.id}`}
          text="View report"
          state={{
            powerbiRelatedReportPageId: widgetData?.powerbiRelatedReportPageId,
          }}
        />
      )}
    </Stack>
  );
};

export default PowerBiWidget;
